import dayjs from "dayjs";

import { PageContext } from "components/detector/ImageQueryPreview";
import { DataChart } from "components/detector/InfoChart/ProjectedMLAcc/PlotChart";

import { DATETIME_FORMAT_READABLE, DATE_FORMAT_READABLE } from "./date";

export const replaceWindowLocation = (url: string) => {
  window.location.replace(url);
};

/**
 * Prefer the Axon SSO end-session URL from janzu when present; otherwise use the
 * legacy Cognito/FusionAuth logout URL from frontend config.
 */
export const getPostLogoutRedirectUrl = (
  logoutResponse: { idp_logout_url?: string } | undefined,
  legacyLogoutUrl: string,
): string => logoutResponse?.idp_logout_url || legacyLogoutUrl;

export const getTimeDifference = (nowDate: Date, previousDate: Date) => nowDate.getTime() - previousDate.getTime();

export function borderText(color: string): string {
  return `
    1px 0 ${color}, -1px 0 ${color}, 0 1px ${color}, 0 -1px ${color},
    1px 1px ${color}, -1px -1px ${color}, 1px -1px ${color}, -1px 1px ${color}
  `;
}

export function parseDateAsUTC(dateString: string): Date {
  // Check if dateString ends with "Z", if not, add it
  const utcDateString = dateString.endsWith("Z") ? dateString : `${dateString}Z`;
  // Create a new Date object, which will be in UTC
  return new Date(utcDateString);
}

export function convertUTCtoLocalReadableDate(utcTimestamp: string | undefined, onlyDate: boolean = false): string {
  if (utcTimestamp) {
    // Check if utcTimestamp ends with "Z", if not, add it
    const timestamp = utcTimestamp.endsWith("Z") ? utcTimestamp : `${utcTimestamp}Z`;

    // Parse the UTC date using dayjs
    const date = dayjs(timestamp);

    // Determine the format string based on onlyDate
    const formatString = onlyDate ? DATE_FORMAT_READABLE : DATETIME_FORMAT_READABLE;
    const readableDate = date.format(formatString);

    // Get the user"s local time offset in minutes and convert it to hours and minutes
    const offsetInMinutes = new Date().getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(offsetInMinutes) / 60);
    const offsetMinutes = Math.abs(offsetInMinutes) % 60;

    // Determine the sign for the offset
    const offsetSign = offsetInMinutes > 0 ? "-" : "+";

    // Format the offset string
    let formattedOffset = `UTC${offsetSign}${offsetHours.toString().padStart(2, "0")}`;
    if (offsetMinutes !== 0) {
      formattedOffset += `:${offsetMinutes.toString().padStart(2, "0")}`;
    }

    return `${readableDate} ${formattedOffset}`;
  }
  return "NA";
}

// Generates an array of tick values based on the provided data.
export function generateTicks(data: DataChart[]) {
  let min = Infinity;

  // Iterate over data to find the minimum value among value and quartiles
  data.forEach((item: DataChart) => {
    const q2Value = item.value;
    const q1Value = item.quartiles[0];
    const q3Value = item.quartiles[1];

    min = Math.min(min, q1Value, q2Value, q3Value);
  });

  // Round down to the nearest multiple of 10
  min = Math.floor(min / 10) * 10;

  // If min is greater than 10 and less than 50, subtract 10 to get the starting tick
  if (min >= 10 && min < 50) {
    min -= 10;
  } else if (min >= 50) {
    min = 50;
  } else {
    // Otherwise, set min to 0 to avoid negative numbers
    min = 0;
  }

  // Create the ticks array
  const ticks = [];
  for (let i = min; i <= 100; i += 10) {
    ticks.push(i);
  }

  return ticks;
}

export function convertToUTC(valueStr: string | undefined, format: string) {
  if (!valueStr) {
    return undefined;
  }
  const originalTimezoneStr = valueStr.slice(-6);
  const originalTimezone = parseInt(originalTimezoneStr, 10); // Convert string to number
  return dayjs(valueStr).utcOffset(originalTimezone).format(format);
}

export function extractNumberFromDetectorName(inputString: string): number | null {
  const match = inputString.match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
}

export function extractPaginateFrom(url: string | undefined | null) {
  if (url) {
    // Create a URL object
    const parsedUrl = new URL(url);

    // Get the "paginate_from" parameter
    return parsedUrl.searchParams.get("paginate_from") || "";
  }
  return "";
}

export function isExplorePage(pageContext: PageContext) {
  return pageContext === PageContext.Explore;
}

// Return the generated image URL
export function createImageURLFromBinary(binaryData: Blob): string {
  if (!binaryData) {
    return "";
  }
  const url = URL.createObjectURL(binaryData);
  return url;
}

export const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
};

export const getFilenameFromResponse = (response: Response, defaultFilename: string) => {
  const contentDisposition = response.headers.get("Content-Disposition");
  const filenameMatch = contentDisposition?.match(/filename="([^"]+)"/);
  return filenameMatch ? filenameMatch[1] : defaultFilename;
};
